<script>
import ModelEdit from "../models/edit-v2";
import FormsModel from "@/components/forms/purchase_plan";
import purchasePlanLoadError from "@/components/mixins/purchase-plan-load-error";
import purchasePlanSubmitError from "@/components/mixins/purchase-plan-submit-error";

export default {
  extends: ModelEdit,
  mixins: [purchasePlanLoadError, purchasePlanSubmitError],
  page: {
    title: "Edit Purchase Plan",
  },
  props: {
    cardTitle: {default() { return this.lodash.startCase(this.$t('purchase_plans.singular')) }},
    resourceRoute: {default: 'purchase_plans'},
    modelVariable: {default: 'purchase_plan'},
    form: {default() { return FormsModel; }}
  },
  data() {
    return {
      confirmPublishResolve: null,
      hasLimitValidationIssue: false,
      formListeners: {
        'limit-validation-change': (value) => { this.hasLimitValidationIssue = value; },
      },
      title: this.lodash.upperFirst(this.$t('purchase_plans.plural')),
    }
  },
  computed: {
    items() {
      return [
        {
          text: this.lodash.upperFirst(this.$t('purchase_plans.plural')),
          href: "/purchase_plans",
        },
        {
          text: this.model && this.model.id ? this.model.name || this.model.id : this.$route.params.key,
          href: `/purchase_plans/${this.$route.params.key}`,
        },
        {
          text: this.$t("Edit"),
          href: `/purchase_plans/${this.$route.params.key}/edit`,
          active: true,
        },
      ]
    }
  },
  mounted() {
    this.fetchModel();
  },
  methods: {
    normalizePayload(value = {}) {
      const payload = {
        ...value,
        _action: value._action || 'draft',
      };

      if ((payload.product_type || '').toLowerCase() === 'x') {
        payload.sales_plan_id = null;
      }

      delete payload.product_type;
      delete payload.product;
      delete payload.partner;
      delete payload.processing;
      delete payload.supervisor;
      delete payload.sales_plan;
      delete payload.approval_template;
      delete payload.purchase_plan_status_histories;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.deleted_at;

      this.lastPurchasePlanSubmitAction = payload._action;

      return payload;
    },
    fetchModel() {
      this.loading = true;
      this.$axios
        .get(`/v1/purchase_plans/${this.$route.params.key}`, {
          params: {
            with: "product,partner,processing.odoo_res_partner.odoo_obligor_limit.odoo_obligor_limit_group,supervisor,sales_plan",
          },
        })
        .then((response) => {
          const { data: { data } } = response;
          this.model = data;
          return response;
        })
        .catch((error) => this.handlePurchasePlanLoadError(error, this.$route.params.key))
        .finally(() => {
          this.loading = false;
        });
    },
    update(value) {
      return this.$axios
        .put(`/v1/purchase_plans/${this.$route.params.key}`, this.normalizePayload(value))
        .catch((e) => {
          if (e.response && e.response.data) {
            this.errors = e.response.data.errors;
          }
          throw e
        });
    },
    confirmPublish() {
      return new Promise((resolve) => {
        this.$bvModal.show('confirm-publish-modal');
        this.confirmPublishResolve = (confirmed) => resolve({ value: confirmed });
      });
    },
    onModalConfirm() {
      if (this.confirmPublishResolve) {
        this.confirmPublishResolve(true);
        this.confirmPublishResolve = null;
      }
    },
    onModalCancel() {
      if (this.confirmPublishResolve) {
        this.confirmPublishResolve(false);
        this.confirmPublishResolve = null;
      }
    },
    onModalHide() {
      if (this.confirmPublishResolve) {
        this.confirmPublishResolve(false);
        this.confirmPublishResolve = null;
      }
    },
    submitWithAction(action) {
      if (action === 'publish' && this.hasLimitValidationIssue) {
        return;
      }

      if (!this.$refs.modelEdit || !this.$refs.modelEdit.$refs.form || !this.$refs.modelEdit.$refs.form.validateAll) {
        return;
      }

      this.$refs.modelEdit.$refs.form.validateAll().then((isValid) => {
        if (!isValid) return;

        const model = {
          ...this.model,
          _action: action,
        };

        if (action === 'publish') {
          this.confirmPublish().then((response) => {
            if (!response.value) return;
            this.handleUpdate(model);
          });
          return;
        }

        this.handleUpdate(model);
      });
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <model-edit
        ref="modelEdit"
        :key="getModelKey()"
        class="col-12"
        :loading="loading"
        :updating="updating"
        v-bind="$props"
        v-model="model"
        :errors.sync="errors"
        :form-listeners="formListeners"
        @back-click="handleBack"
        @update-click="handleUpdate"
        @model-changed="handleModelChanged"
      >
        <template #card-footer="{ updating, denyBack, handleBack }">
          <div class="row footer-wrapper">
            <div class="col-12 d-flex flex-row-reverse justify-content-start flex-wrap">
              <b-button :disabled="updating || hasLimitValidationIssue" class="ms-2" variant="primary" @click="submitWithAction('publish')">
                <i v-if="updating" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                {{ $t('purchase_plans.publish') }}
              </b-button>
              <b-button :disabled="updating" class="ms-2 border-0" variant="primary" @click="submitWithAction('draft')" style="background-color: #FDB022">
                <i v-if="updating" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                {{ $t('Update') }}
              </b-button>
              <b-button class="ms-2" v-if="!denyBack" variant="link" @click="handleBack">
                {{ $t('Back') }}
              </b-button>
            </div>
          </div>
        </template>
      </model-edit>
    </div>

    <b-modal
      id="confirm-publish-modal"
      ref="confirmPublishModal"
      centered
      hide-header
      no-close-on-backdrop
      body-class="p-4"
      footer-class="p-0 border-0"
      @ok="onModalConfirm"
      @cancel="onModalCancel"
      @hide="onModalHide"
    >
      <div class="text-center py-2">
        <div class="mb-2 d-flex justify-content-center">
          <div class="d-flex justify-content-center align-items-center p-2 rounded rounded-circle bg-green-200" style="width: 48px; height: 48px;">
            <div class="d-flex justify-content-center align-items-center p-2 rounded rounded-circle" style="background: #15803D; width: 24px; height: 24px;">
              <i class="bx bx-check text-green-200" style="font-size: 24px;"></i>
            </div>
          </div>
        </div>
        <h4 class="mb-2">{{ $t('purchase_plans.confirm_publish_title') }}</h4>
        <p class="text-muted mb-0">{{ $t('purchase_plans.confirm_publish_text') }}</p>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <div class="d-flex gap-3 w-100 p-3">
          <b-button variant="light" class="w-50" @click="cancel()">
            {{ $t('purchase_plans.cancel') }}
          </b-button>
          <b-button variant="primary" class="w-50" @click="ok()">
            {{ $t('purchase_plans.yes_continue') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <purchase-plan-load-error-dialog
        ref="purchasePlanLoadErrorDialog"
    />
    <purchase-plan-publish-limit-dialog
        ref="purchasePlanPublishLimitDialog"
    />
  </Layout>
</template>

<style scoped lang="scss">
</style>
